import { Alert, CircularProgress } from "@mui/material";
import React, { ReactElement, useState } from "react";
import { ErrorCode, FileRejection, useDropzone } from "react-dropzone";

import Button from "../components/button/button";
import CloudUpload from "../components/icons/cloud-upload.svg";
import ArrowDownIcon from "../components/icons/outlines/arrow-down";
import TextInput from "../components/inputs/text-input";
import Modal from "../components/modal/modal";
import AssetUploadPreview from "../components/upload/asset-upload-preview";
import useLocale from "../hooks/use-locale";
import useNotification from "../hooks/use-notification";
import { classNames } from "../utils/classes";

function AssetUploadSetFile({
  onFileChange,
}: {
  onFileChange: (file: File) => void;
}): ReactElement {
  const notification = useNotification();
  const { getText } = useLocale();

  function onError(errorCode: ErrorCode | string): void {
    let errorTextPrefix;
    switch (errorCode) {
      case ErrorCode.FileInvalidType: {
        errorTextPrefix =
          "sections.assetUpload.notification.errors.fileInvalidType";
        break;
      }
      case ErrorCode.TooManyFiles: {
        errorTextPrefix =
          "sections.assetUpload.notification.errors.singleAssetSelect";
        break;
      }
      case ErrorCode.FileTooLarge: {
        errorTextPrefix =
          "sections.assetUpload.notification.errors.fileTooLarge";
        break;
      }
      default: {
        errorTextPrefix =
          "sections.assetUpload.notification.errors.genericError";
        break;
      }
    }

    notification.addError(
      getText(`${errorTextPrefix}.body`),
      getText(`${errorTextPrefix}.headline`),
    );
  }

  function onDrop(
    acceptedFiles: File[],
    fileRejections: FileRejection[],
  ): void {
    if (fileRejections.length > 0) {
      const fileRejection = fileRejections[0];
      onError(fileRejection.errors[0].code);
    } else {
      onFileChange(acceptedFiles[0]);
    }
  }

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/jpeg": [],
      "image/jpg": [],
      "image/png": [],
      "image/webp": [],
    },
    maxFiles: 1,
    maxSize: 10_485_760,
    onDrop,
  });

  return (
    <div className="flex flex-col">
      <div
        {...getRootProps({ className: "dropzone" })}
        className="flex flex-col items-center space-y-3 rounded-xl bg-companyLightGrey p-3"
      >
        <img src={CloudUpload} alt="" width={220} height={220} />
        <input {...getInputProps()} />
        <p className="text-center text-xl text-companyMediumGrey">
          {getText("sections.assetUpload.drag")}
          <br />
          {getText("sections.assetUpload.or")}
        </p>
        <Button
          onClick={() => {}}
          label={getText("sections.assetUpload.action")}
        />
      </div>
      <Alert className="mt-5 rounded-xl" severity="info">
        Supported files are images in jpg or png with sRGB colour profile, up to
        sizes of 6000px px or 10 MB.
      </Alert>
    </div>
  );
}

function AssetUploadFileMetaData({
  file,
  assetName,
  assetNameChange,
  assetNameExample,
}: {
  file: File;
  assetName: string;
  assetNameChange: (name: string) => void;
  assetNameExample: string;
}): ReactElement {
  const { getText } = useLocale();

  return (
    <form className="flex h-full flex-col">
      <div className="flex flex-1 flex-col items-center justify-center space-y-5 rounded-xl p-3">
        <AssetUploadPreview file={file} />
        <div className="flex flex-col space-y-1">
          <p className="text-base text-companyDarkGrey">
            {getText("sections.assetUpload.form.name")}
          </p>
          <TextInput
            placeholder=""
            id="name"
            onChange={(v) => assetNameChange(v)}
            value={assetName}
            //disabled={isUploading}
          />
          <p className="text-xs text-companyDarkGrey">{assetNameExample}</p>
        </div>
      </div>
    </form>
  );
}

export default function AssetUploadModal(props: {
  headline: string;
  open: boolean;
  onClose(): void;
  createAsset: (name: string, file: File) => Promise<void>;
  formNameExample: string;
}): ReactElement {
  const { headline, open, onClose, createAsset, formNameExample } = props;

  const notification = useNotification();
  const { getText } = useLocale();

  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const [assetName, setAssetName] = useState<string>("");

  function resetValues(): void {
    setFile(null);
    setAssetName("");
    setIsUploading(false);
  }

  function handleOnClose(): void {
    if (isUploading) {
      alert(
        "Please wait for the upload to finish, before you close this window.",
      );

      return;
    }

    onClose();
    resetValues();
  }

  async function onSubmit(): Promise<void> {
    if (!file) {
      return;
    }

    try {
      setIsUploading(true);
      await createAsset(assetName, file);

      notification.addSuccess(
        getText("sections.assetUpload.notification.success.body"),
        getText("sections.assetUpload.notification.success.headline"),
      );

      // Close modal and reset values after successful upload
      onClose();
      resetValues();
    } catch (error) {
      notification.addError(
        error instanceof Error && error.message
          ? error.message
          : getText(
              "sections.assetUpload.notification.errors.singleAssetUpload.body",
            ),
        getText(
          "sections.assetUpload.notification.errors.singleAssetUpload.headline",
        ),
      );

      setIsUploading(false);
    }
  }

  // Determine content to show in modal based on current state
  let content;
  if (isUploading) {
    content = (
      <div className="flex flex-col justify-center items-center">
        <CircularProgress size="100px" />
        <h2 className="mt-3 text-xl">
          {getText("sections.assetUpload.uploading")}
        </h2>
      </div>
    );
  } else if (file) {
    content = (
      <div className="w-full h-full flex flex-col">
        <AssetUploadFileMetaData
          file={file}
          assetName={assetName}
          assetNameChange={setAssetName}
          assetNameExample={formNameExample}
        />
        <div className={classNames("mt-3 flex space-x-2", "justify-between")}>
          <button
            className="flex cursor-pointer flex-row items-center"
            onClick={() => setFile(null)}
            type="button"
            disabled={isUploading}
          >
            <ArrowDownIcon className="rotate-90 fill-companyDarkGrey" />
            <p className="text-base uppercase text-companyDarkGrey">
              {getText("sections.assetUpload.back")}
            </p>
          </button>

          <Button
            onClick={() => onSubmit()}
            label={getText("sections.assetUpload.upload")}
            disabled={!assetName || isUploading}
          />
        </div>
      </div>
    );
  } else {
    content = (
      <AssetUploadSetFile onFileChange={(newFile) => setFile(newFile)} />
    );
  }

  return (
    <Modal
      open={open}
      headline={headline}
      onClose={() => handleOnClose()}
      closeDisabled={isUploading}
      className="h-[550px] max-w-xl"
    >
      <div className="flex h-full items-center justify-center">{content}</div>
    </Modal>
  );
}
